import { formatDateTime, formatFileSize, getFileExtension, isOptimizedItem, type MediaItem } from '@/lib/media';

export interface FileKind {
  label: string;
  icon: string;
  tone: string;
}

export function resolveFileKind(fileName: string): FileKind {
  const ext = getFileExtension(fileName);
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg', 'bmp'].includes(ext)) {
    return { label: '画像', icon: 'ri-image-line', tone: 'bg-primary-100 text-primary-700' };
  }
  if (['mp4', 'mov', 'webm', 'avi', 'mkv'].includes(ext)) {
    return { label: '動画', icon: 'ri-video-line', tone: 'bg-accent-100 text-accent-700' };
  }
  if (['pdf'].includes(ext)) {
    return { label: 'PDF', icon: 'ri-file-pdf-2-line', tone: 'bg-secondary-100 text-secondary-800' };
  }
  if (['doc', 'docx', 'txt', 'md'].includes(ext)) {
    return { label: '文書', icon: 'ri-file-text-line', tone: 'bg-secondary-100 text-secondary-800' };
  }
  if (['zip', 'rar', '7z'].includes(ext)) {
    return { label: 'アーカイブ', icon: 'ri-file-zip-line', tone: 'bg-secondary-100 text-secondary-800' };
  }
  return { label: ext ? ext.toUpperCase() : 'ファイル', icon: 'ri-file-line', tone: 'bg-secondary-100 text-secondary-800' };
}

interface Props {
  item: MediaItem;
  onOpen: (item: MediaItem) => void;
  onDelete: (item: MediaItem) => void;
}

export default function MediaCard({ item, onOpen, onDelete }: Props) {
  const kind = resolveFileKind(item.fileName || item.key);
  const optimized = isOptimizedItem(item);

  return (
    <div className="group relative overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-background-300">
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="block w-full cursor-pointer text-left"
      >
        <div className="flex h-40 items-center justify-center overflow-hidden bg-background-100">
          {kind.label === '画像' ? (
            <img
              src={item.url}
              alt={item.fileName}
              className="h-full w-full object-cover object-top"
            />
          ) : (
            <span className={`flex h-12 w-12 items-center justify-center rounded-lg ${kind.tone}`}>
              <i className={`${kind.icon} text-2xl`} />
            </span>
          )}
        </div>

        <div className="space-y-2 p-3">
          <p className="truncate text-sm font-medium text-foreground-900" title={item.fileName}>
            {item.fileName}
          </p>
          <div className="flex items-center justify-between gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${
                optimized
                  ? 'bg-accent-100 text-accent-800'
                  : 'bg-primary-100 text-primary-700'
              }`}
            >
              <span className="flex h-3 w-3 items-center justify-center">
                <i className={optimized ? 'ri-magic-line' : 'ri-file-line'} />
              </span>
              {optimized ? '最適化済み' : 'オリジナル'}
            </span>
          </div>
          <p className="text-[11px] text-foreground-400">
            {formatFileSize(item.size)} ・ {formatDateTime(item.lastModified)}
          </p>
        </div>
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item);
        }}
        className="absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-background-50/90 text-foreground-600 opacity-0 transition-opacity hover:bg-accent-600 hover:text-background-50 group-hover:opacity-100"
        aria-label="削除"
      >
        <i className="ri-delete-bin-line" />
      </button>
    </div>
  );
}